import {
  Table,
  Row,
  Col,
  Button,
  Badge,
  Tag,
  Select,
  DatePicker,
  Popconfirm,
} from 'antd';
import { FC, Key, useState } from 'react';
import { RentResponse } from '../../../api/client/data-contracts';
import { useHistory } from 'react-router-dom';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { UseRentsFilterResult } from './useRentsFilter';
import { TableProps } from 'antd/lib';
import { getUserData } from 'src/api/auth';
import { useRents } from './useRents';

const { Column } = Table;
export const getDatePortion = (date: string | Date) =>
  new Date(date).toUTCString().substring(0, 10);

export function getStateString(state: RentResponse['state']) {
  switch (state) {
    case 'ORDERING':
      return <Badge status='default' text='Encomendando' />;
    case 'NOT_STARTED':
      return <Badge status='default' text='Reservado' />;
    case 'ONGOING':
      return <Badge status='processing' text='Em curso' />;
    case 'ENDED':
      return <Badge status='success' text='Concluído' />;
  }
}

export function getPayedRentTag(payed: boolean) {
  return payed ? (
    <Tag color='success' className='hp-px-12'>
      <span className='hp-d-flex hp-align-items-center'>
        <span className='hp-ml-4'>Pago</span>
      </span>
    </Tag>
  ) : (
    <Tag color='processing' className='hp-px-12'>
      <span className='hp-d-flex hp-align-items-center'>
        <span className='hp-ml-4'>Por pagar</span>
      </span>
    </Tag>
  );
}

interface Props {
  useRentsFilterResult: UseRentsFilterResult;
}
const RentsTable: FC<Props> = ({ useRentsFilterResult }) => {
  const history = useHistory();
  function openRent(rentId: string) {
    history.push(`/apps/cockpit/rents/${rentId}`);
  }
  const data =
    useRentsFilterResult.filteredRentsQuery.data?.map((rent: RentResponse) => ({
      key: rent.id,
      customer: rent.customer.description,
      code: rent.code,
      referenceCode: rent.referenceCode,
      number: rent.rentNumber,
      state: getStateString(rent.state),
      startDate: <FormattedDate value={rent.startDate} />,
      endDate: <FormattedDate value={rent.endDate} />,
      paymentValue: (
        <p className='hp-mb-0 hp-text-color-primary-1 hp-font-weight-500'>
          {rent.paymentTotal} €
        </p>
      ),
      paymentValueWithVat: (
        <p className='hp-mb-0 hp-text-color-primary-1 hp-font-weight-500'>
          {rent.paymentTotalWithVat} €
        </p>
      ),
      numberOfEquipments: rent.rentLines.length,
      payed: getPayedRentTag(rent.payed),
      actions: (
        <Row>
          <Col md={12} span={24} className='hp-pr-sm-0 hp-pr-12'>
            <Button
              onClick={() => openRent(rent.id)}
              block
              type='primary'
              htmlType='submit'
            >
              <FormattedMessage id='open' />
            </Button>
          </Col>
        </Row>
      ),
    })) || [];

  const loading =
    useRentsFilterResult.filteredRentsQuery.isLoading ||
    useRentsFilterResult.filteredRentsQuery.isFetching;

  const userData = getUserData();

  const canBulkUpdateRent =
    userData?.permissions.includes('rent:bulk-update') || userData?.isAdmin;

  const onChange: Required<
    TableProps<(typeof data)[number]>
  >['rowSelection']['onChange'] = (
    selectedRowsKeys: Key[],
    selectedRows: (typeof data)[number][],
  ) => {
    setSelectedRents(selectedRowsKeys as string[]);
  };
  const [selectedRents, setSelectedRents] = useState<string[]>([]);

  const { bulkDeleteRents } = useRents();

  return (
    <>
      {selectedRents.length > 0 && (
        <Popconfirm
          title='Tem a certeza que pretende eliminar os aluguers selecionados?'
          onConfirm={() => bulkDeleteRents(selectedRents)}
          okText='Sim'
          cancelText='Não'
        >
          <Button danger>Eliminar selecionados</Button>
        </Popconfirm>
      )}
      <Table
        dataSource={data}
        rowSelection={
          canBulkUpdateRent
            ? { type: 'checkbox', onChange: onChange }
            : undefined
        }
        loading={loading}
      >
        {[
          { label: 'Cliente', field: 'customer' },
          { label: 'Estado', field: 'state' },
          { label: 'Início', field: 'startDate' },
          { label: 'Fim', field: 'endDate' },
          { label: 'Código', field: 'code' },
          { label: 'Referência', field: 'referenceCode' },
          { label: 'Número de equipamentos', field: 'numberOfEquipments' },

          { label: 'Valor', field: 'paymentValue' },
          { label: 'Com IVA', field: 'paymentValueWithVat' },
          { label: 'Estado do pagamento', field: 'payed' },
          { label: 'Ações', field: 'actions' },
        ].map((column) => (
          <Column
            title={column.label}
            dataIndex={column.field}
            key={column.field}
          />
        ))}
      </Table>
    </>
  );
};

export default RentsTable;
